import { useRef, useState } from 'react';
import { Button, Modal, Spinner } from '@heroui/react';
import type { VisitsImportResult } from '../useVisitsPage';

interface ImportVisitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isImporting: boolean;
  importResult: VisitsImportResult | null;
  onImport: (file: File) => void;
}

export function ImportVisitsModal({
  isOpen,
  onClose,
  isImporting,
  importResult,
  onImport,
}: ImportVisitsModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setFile(null);
      onClose();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
    e.target.value = ''; // повторный выбор того же файла тоже вызывает onChange
  };

  const handleSubmit = () => {
    if (file && !isImporting) onImport(file);
  };

  return (
    <Modal.Backdrop isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Modal.Container size="md">
        <Modal.Dialog className="sm:max-w-lg" aria-label="Импорт визитов из XLSX">
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>Импорт визитов</Modal.Heading>
          </Modal.Header>
          <Modal.Body>
            <div className="flex flex-col gap-4">
              <p className="text-sm text-muted">
                XLSX-файл с колонками «Название шаблона», «Логин исполнителя», «Дата визита», «ID
                локации» (первая строка — заголовки). Исполнитель ищется по логину, локация — по
                внутреннему ID клиента; визиты создаются только из запущенных шаблонов, неизвестные
                строки пропускаются с ошибкой.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={handleFileChange}
              />
              <div className="flex items-center gap-3">
                <Button variant="outline" onPress={() => fileInputRef.current?.click()}>
                  Выбрать файл
                </Button>
                {file && <span className="truncate text-sm text-foreground">{file.name}</span>}
              </div>
              {importResult && (
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-foreground">Импортировано: {importResult.imported}</p>
                  {importResult.errors.length > 0 && (
                    <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-[var(--radius)] bg-surface-secondary p-3 text-sm text-danger">
                      {importResult.errors.map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="tertiary" onPress={onClose}>
              Закрыть
            </Button>
            <Button isDisabled={!file || isImporting} onPress={handleSubmit}>
              {isImporting ? <Spinner size="sm" /> : 'Импортировать'}
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
